import { useEffect, useMemo, useState } from 'react';
import moment from 'moment';

import { NormalizedFile } from '@/types/file-explorer-types';

import IconClose from '@/assets/svg/icon-close.svg?react';
import IconImg from '@/assets/svg/icon-img.svg?react';

import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { useApiManagerStore } from '@/stores/apiManagerStore';
import { useFileExplorerStore } from '@/stores/fileExplorerStore';
import { useGlobalStore } from '@/stores/globalStore';

import { FILE_TYPE_NAMES } from '@/constants/global-constants';

import { Textarea } from '../ui/textarea';

interface DetailsFileInfoProps {
  isOpen: boolean;
  onClose: () => void;
  data: NormalizedFile | null;
  setFileInfo: (file: NormalizedFile | null) => void;
}

const DetailsFileInfo = ({
  isOpen,
  onClose,
  data,
  setFileInfo,
}: DetailsFileInfoProps) => {
  const { users } = useGlobalStore();
  const { agreementsState, setFilesState, filesState } = useFileExplorerStore();
  const { updateFile, getAgreements } = useApiManagerStore();

  const [isEditingDescription, setIsEditingDescription] = useState(false);
  const [newDescription, setNewDescription] = useState('');
  const [fetchedLocation, setFetchedLocation] = useState<string>('');

  const getUserName = useMemo(() => {
    if (!users || !data?.user) return '';

    const userWithUuid = users.find((user) => user?.uuid === data?.user);
    if (!userWithUuid) return '';

    return (
      (userWithUuid.first_name || '') + ' ' + (userWithUuid.last_name || '')
    );
  }, [users, data?.user]);

  const fileLocation = useMemo(() => {
    const agreement = agreementsState.entities[data?.agreement ?? ''];
    if (!agreement) return fetchedLocation;

    return agreement.breadcrumb_name || agreement.name || '';
  }, [agreementsState, data?.agreement, fetchedLocation]);

  // Fetch agreement data if not in store (e.g. for auto-archived agreements)
  useEffect(() => {
    const fetchAgreementIfMissing = async () => {
      if (!data?.agreement) return;
      const agreement = agreementsState.entities[data.agreement];
      if (agreement) return;

      try {
        const agreements = await getAgreements([data.agreement]);
        if (agreements && agreements.length > 0) {
          setFetchedLocation(agreements[0].name || '');
        }
      } catch {
        // Agreement may not be accessible (e.g. archived) - gracefully handle
        setFetchedLocation('');
      }
    };

    fetchAgreementIfMissing();
  }, [data?.agreement, agreementsState.entities, getAgreements]);

  const renderContent = () => {
    if (!data) {
      return (
        <>
          <div className="space-y-2">
            <Skeleton className="h-6 w-48" />
            <Skeleton className="h-4 w-72" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-36" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-32" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-4 w-52" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-36" />
            <Skeleton className="h-4 w-52" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-48" />
          </div>
          <div className="space-y-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-4 w-48" />
          </div>
        </>
      );
    }

    return (
      <>
        <div className="space-y-0">
          <span className="text-base font-medium text-gray-200">
            Місце розташування
          </span>
          <div className="flex items-center">
            <span className="">{fileLocation}</span>
          </div>
        </div>

        <div className="space-y-0">
          <span className="text-base font-medium text-gray-200">Тип</span>
          <div className="flex items-center">
            <span className="">
              {data?.file_type
                ? FILE_TYPE_NAMES[
                    data.file_type as keyof typeof FILE_TYPE_NAMES
                  ]
                : ''}
            </span>
          </div>
        </div>

        <div className="space-y-0">
          <span className="text-base font-medium text-gray-200">Формат</span>
          <div className="flex items-center">
            <span className="">{data?.format}</span>
          </div>
        </div>

        <div className="space-y-0">
          <span className="text-base font-medium text-gray-200">Розмір</span>
          <div className="flex items-center">
            <span className="">{data?.size}</span>
          </div>
        </div>
        {data?.width && data?.height && (
          <div className="space-y-0">
            <span className="text-base font-medium text-gray-200">
              Розширення
            </span>
            <div className="flex items-center">
              <span className="">
                {data?.width} x {data?.height}
              </span>
            </div>
          </div>
        )}

        <div className="space-y-0">
          <span className="text-base font-medium text-gray-200">
            Координати
          </span>
          <div className="flex items-center">
            <span className="">
              {data?.gps_longitude && data?.gps_latitude
                ? `${data?.gps_longitude}, ${data?.gps_latitude}`
                : ''}
            </span>
          </div>
        </div>

        <div className="flex flex-col">
          <span className="text-base font-medium text-gray-200">
            Дата зйомки
          </span>
          <span className="">
            {data?.filmed_date
              ? moment(data.filmed_date).format('DD.MM.YYYY HH:mm')
              : '-'}
          </span>
        </div>

        <div className="flex flex-col">
          <span className="text-base font-medium text-gray-200">
            Дата завантаження
          </span>
          <span className="">
            {moment(data?.upload_date).format('DD.MM.YYYY HH:mm')}
          </span>
        </div>

        <div className="space-y-0">
          <span className="text-base font-medium text-gray-200">Автор</span>
          <div className="flex items-center">
            <span className="">{getUserName}</span>
          </div>
        </div>

        <div className="flex flex-col justify-between space-y-0">
          <div className="mb-2 flex items-center justify-between">
            <span className="text-base font-medium text-gray-200">Опис</span>
            <Button
              variant="outline"
              size="sm"
              type="button"
              onClick={() => setIsEditingDescription(true)}
            >
              {data?.description ? 'Змінити опис' : 'Додати опис'}
            </Button>
          </div>
          <div className="flex items-center">
            <span className="max-h-[300px] overflow-y-auto break-all">
              {data?.description}
            </span>
          </div>
        </div>
      </>
    );
  };

  const renderDescription = () => {
    return (
      <div className="flex flex-col gap-4">
        <Textarea
          value={newDescription}
          onChange={(e) => setNewDescription(e.target.value)}
          placeholder="Введіть ваш коментар..."
          className="min-h-[450px]"
        />
        <div className="flex justify-between gap-2">
          <Button
            variant="outline"
            className="h-[40px] w-[180px]"
            type="button"
            onClick={handleAddDescription}
            disabled={!newDescription?.trim()}
            size="sm"
          >
            {data?.description ? 'Змінити опис' : 'Додати опис'}
          </Button>
          <Button
            variant="destructive"
            className="h-[40px] w-[180px]"
            type="button"
            onClick={handleCancelDescription}
            size="sm"
          >
            Скасувати
          </Button>
        </div>
      </div>
    );
  };

  const handleAddDescription = async () => {
    if (!data) return;
    try {
      const newFile = await updateFile(data.uuid, {
        ...data,
        description: newDescription,
      });

      setFilesState({
        ...filesState,
        entities: {
          ...filesState.entities,
          [data.uuid]: {
            ...data,
            ...newFile,
          },
        },
        rootIds: filesState.rootIds,
      });

      setFileInfo({ ...data, ...newFile });
      setNewDescription('');
      setIsEditingDescription(false);
    } catch (error) {
      console.error('Failed to update file description:', error);
    }
  };

  const handleCancelDescription = () => {
    setNewDescription(data?.description || '');
    setIsEditingDescription(false);
  };

  useEffect(() => {
    setNewDescription(data?.description || '');
  }, [data]);

  useEffect(() => {
    return () => {
      onClose();
    };
  }, []);

  return (
    <div
      className={`absolute right-[-50px] top-0 w-full transform overflow-hidden rounded-md bg-white shadow-sm transition-transform duration-300 ease-in-out sm:w-[524px] ${
        isOpen ? 'translate-x-[-50px]' : 'translate-x-full'
      } z-50 h-full`}
    >
      <div className="flex h-full flex-col">
        <div className="flex-shrink-0 border-b pb-5 pl-9 pr-5 pt-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <IconImg className="h-[14px] min-w-[18px] fill-dark-red" />
              <h2
                className="block max-w-[400px] items-center gap-4 break-all text-xl font-medium"
                title={data?.name}
              >
                {data ? data.name : <Skeleton className="h-6 w-48" />}
              </h2>
            </div>

            <Button
              variant="ghost"
              size="icon"
              onClick={onClose}
              className="p-1.5"
            >
              <IconClose className="h-4 w-4" />
            </Button>
          </div>
          <span className="mt-[28px] block text-base font-semibold">
            Деталі файлу
          </span>
        </div>

        <div className="flex-grow overflow-auto">
          <div className="custom-scrollbar flex flex-col space-y-[18px] px-9 pb-6 pt-4">
            {isEditingDescription ? renderDescription() : renderContent()}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DetailsFileInfo;
